import { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { compressImage, getImageBlobFromClipboard, type CompressionOptions } from "@/lib/imageCompression";
import { uploadImage } from "@/services/storageService";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export interface ImageUploadFieldProps {
  value: string[];
  onChange: (urls: string[]) => void;
  folder: string;
  maxFiles?: number;
  compressionOptions?: CompressionOptions;
  className?: string;
  /** Etiqueta para la zona de pegar / seleccionar */
  label?: string;
}

export function ImageUploadField({
  value,
  onChange,
  folder,
  maxFiles = 15,
  compressionOptions = {},
  className,
  label = "Imágenes",
}: ImageUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const doAddImage = useCallback(
    async (blob: Blob) => {
      if (value.length >= maxFiles) {
        toast.error(`Máximo ${maxFiles} imágenes`);
        return;
      }
      setUploading(true);
      try {
        const compressed = await compressImage(blob, {
          maxWidth: compressionOptions.maxWidth ?? 1920,
          maxHeight: compressionOptions.maxHeight ?? 1080,
          quality: compressionOptions.quality ?? 0.8,
          maxFileKB: compressionOptions.maxFileKB ?? 500,
        });
        const result = await uploadImage(compressed, folder);
        onChange([...value, result.url]);
        toast.success("Imagen agregada");
      } catch (e) {
        console.error(e);
        toast.error("Error al procesar o subir la imagen");
      } finally {
        setUploading(false);
      }
    },
    [value, maxFiles, folder, onChange, compressionOptions]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const blob = getImageBlobFromClipboard(e.nativeEvent);
      if (blob) {
        e.preventDefault();
        doAddImage(blob);
      }
    },
    [doAddImage]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;
      const file = files[0];
      if (!file.type.startsWith("image/")) {
        toast.error("Selecciona una imagen");
        return;
      }
      doAddImage(file);
      e.target.value = "";
    },
    [doAddImage]
  );

  const remove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  return (
    <div className={cn("space-y-2", className)}>
      {label ? <label className="text-sm font-medium">{label}</label> : null}
      <div
        tabIndex={0}
        role="button"
        onPaste={handlePaste}
        className={cn(
          "flex min-h-[120px] flex-wrap items-center gap-2 rounded-lg border border-dashed border-input bg-muted/30 p-3 focus:outline-none focus:ring-2 focus:ring-ring",
          value.length >= maxFiles && "opacity-70"
        )}
      >
        {value.map((url, i) => (
          <div key={url} className="relative group">
            <img
              src={url}
              alt=""
              className="h-20 w-20 rounded-md object-cover border border-border"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -right-1 -top-1 h-5 w-5 rounded-full opacity-90 group-hover:opacity-100"
              onClick={() => remove(i)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
        {value.length < maxFiles && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-md border border-dashed border-input bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <ImagePlus className="h-6 w-6" />
                  <span className="text-[10px] mt-0.5">o pegar</span>
                </>
              )}
            </button>
          </>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Pega desde el portapapeles (Ctrl+V) o haz clic para seleccionar. Máx. {maxFiles}. Se comprimen automáticamente.
      </p>
    </div>
  );
}
